import {
  getRelationshipLabel,
  getNestedSubelementsByType,
  getNextUuid,
  getPersonName,
  getPersonRelationships,
  getImage,
  getNestedOrFloatingSubelementsById,
  getNestedOrFloatingElementById,
} from '@fs/zion-sls'
import { i18n } from '@fs/zion-locale'
import { getCommonRelationships } from '../../../../views/indexing/components/editPanels/editRelationships/utils/EditRelationships.utls'
import { MAKE_NEW_RECORD, NO_RELATION_TYPE } from './saveRelationships'

export const NO_RELATION_SUGGESTION = {
  key: NO_RELATION_TYPE,
  primaryText: i18n.t('types.no_relation', 'No Relation'),
  group: i18n.t('household-details-card.other-group', 'Other Relationships'),
  sort: 3,
}

/**
 * Retrieves the list of persons and their relationships for a given household.
 *
 * If no relationship exists between the focused person and another person in the household,
 * a default "No Relation" entry is created for that person.
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.person - The focused person object.
 * @param {string|number} params.recordId - The ID of the focused person record.
 * @param {Array<Object>} params.allRecords - Array of all images records.
 * @param {Array<Object>} params.images - Array of SLS images.
 * @param {string} params.currentPartitionKey - The partition key of the focused person.
 * @param {Array<string>} [params.includedPersonIds=[]] - Optional array of additional person IDs to include as "No Relation".
 * @param {Array<string>} [params.excludedPersonIds=[]] - Optional array of person IDs to exclude from the relationships.
 * @param {Array<Object>} [params.newlyCreatedRelationships=[]] - Optional array of newly created relationship objects.
 * Has priority over includedPersonIds.
 * @returns {Object} An object containing:
 *   - relationships: Array of relationship objects for the household.
 *   - record: The record object for the focused person's household.
 */
export const getPersonsAndRelationshipsForHousehold = ({
  person,
  recordId,
  allRecords,
  images,
  currentPartitionKey,
  includedPersonIds = [],
  excludedPersonIds = [],
  newlyCreatedRelationships = [],
}) => {
  let relationships = []
  let record = { id: recordId, partitionKey: currentPartitionKey }

  // If recordId is not MAKE_NEW_RECORD, find the record and process existing relationships
  if (recordId !== MAKE_NEW_RECORD) {
    record = allRecords.find((rec) => rec.id === recordId)

    // If no record found, return empty array because there are no persons to process
    if (!record) return { relationships: [], record: null }

    // Get existing relationships for the focused person
    const existingRelationships = getPersonRelationships(person, images, true)

    // Create a map for quick lookup of existing relationships
    const existingRelationshipsMap = new Map()
    existingRelationships.forEach((rel) => {
      existingRelationshipsMap.set(rel.otherPersonId, rel)
    })

    const allRecordPersons =
      getNestedSubelementsByType(record, 'PERSON')?.filter(
        (p) => p?.id !== person?.id && !excludedPersonIds.includes(p?.id)
      ) || []

    relationships = allRecordPersons.map((recordPerson) => {
      const existingRel = existingRelationshipsMap.get(recordPerson.id)

      // If an existing relationship is found, merge it with the person info...
      if (existingRel) {
        return { ...existingRel, person: recordPerson }
      }

      // ...Otherwise create a NO_RELATION entry
      return {
        fieldType: NO_RELATION_TYPE,
        relKey: NO_RELATION_TYPE,
        label: i18n.t('household-details-card.no-relation', 'No Relation'),
        value: getPersonName({ person: recordPerson, justOneName: true }),
        id: null,
        otherPersonId: recordPerson.id,
        person: recordPerson,
      }
    })
  }

  // Add additionalPersonIds as NO_RELATION if not already present
  const processedIncludedPersonIds = includedPersonIds.filter(
    (id) => !excludedPersonIds.includes(id) || id === person?.id
  )

  if (processedIncludedPersonIds.length > 0) {
    // Collect all person IDs already present in relationships
    const existingIds = new Set(relationships.map((rel) => rel.otherPersonId))
    processedIncludedPersonIds.forEach((personId) => {
      if (!existingIds.has(personId)) {
        // Use getImage and getNestedOrFloatingSubelementsById for lookup
        const image = getImage(images, currentPartitionKey)
        let foundPerson = null

        if (personId?.includes('NEW_PERSON')) {
          const relationship = newlyCreatedRelationships.find((rel) => rel.personId === personId)
          foundPerson = relationship
            ? { id: personId, localizedName: Object.values(relationship.newPerson).filter(Boolean).join(' ') }
            : null
        } else {
          foundPerson = getNestedOrFloatingSubelementsById(image, personId, 'PERSON')?.[0] || null
        }

        if (foundPerson) {
          relationships.push({
            fieldType: NO_RELATION_TYPE,
            relKey: NO_RELATION_TYPE,
            label: i18n.t('household-details-card.no-relation', 'No Relation'),
            value: getPersonName({ person: foundPerson, justOneName: true }),
            id: getNextUuid(),
            otherPersonId: foundPerson.id,
            person: foundPerson,
          })
        }
      }
    })
  }

  return { relationships, record }
}

/**
 * Generates a list of household suggestions from the provided records.
 * Each suggestion represents a household, identified by a primary person (if available) or the first person in the record.
 *
 * @param {Object} params - The parameters object.
 * @param {Array<Object>} params.records - Array of SLS records.
 * @param {Object} [params.person] - Optional person object to create a "New Household" suggestion.
 * @returns {Array<{ key: string, primaryText: string }>} An array of household suggestion objects,
 * each containing a unique key and the primary person's name as primaryText.
 */
export const getHouseholdSuggestions = ({ records = [], person }) => {
  const households = new Set()

  // Add the "New Household" option if the person is new or has no recordId
  const newHousehold =
    person && (!person?.recordId || person?.recordId === MAKE_NEW_RECORD)
      ? {
          key: MAKE_NEW_RECORD,
          primaryText: i18n.t('household-details-card.new-household', 'New Household: {personName}', {
            personName: getPersonName({ person, justOneName: true }),
          }),
          partitionKey: person?.partitionKey,
        }
      : null

  if (newHousehold) {
    households.add(newHousehold)
  }

  records.forEach((record) => {
    const recordPersons = getNestedSubelementsByType(record, 'PERSON')
    // Select primary person or first as representative of the household
    const primaryPerson = recordPersons.find((recordPerson) => recordPerson.primary) || recordPersons[0]

    if (primaryPerson) {
      const personName = getPersonName({ person: primaryPerson, justOneName: true })
      households.add({
        key: record.id,
        primaryText: personName,
        record,
        partitionKey: record.partitionKey,
      })
    }
  })

  return Array.from(households)
}

export const prepareRelationshipsFields = (relationships) => {
  let formIdCounter = 1

  const relDefaults =
    relationships?.map((rel) => {
      const { relKey, order } = parseRelKey(rel?.relKey)
      return {
        formId: `rel_${formIdCounter++}`,
        slsRelationshipId: rel?.id || null,
        personId: rel?.otherPersonId || null,
        recordId: rel?.person?.recordId || null,
        relationshipType: relKey || null,
        relationshipDirection: order || null,
        relationship: rel?.label ? { key: rel?.relKey, primaryText: rel?.label } : null,
        value: rel?.value ? { key: rel?.otherPersonId, primaryText: rel?.value } : null,
        isNew: !rel?.id && rel?.relKey !== NO_RELATION_TYPE,
        isDeleted: false,
        secondaryPersonPrimaryStatusChange: null,
      }
    }) || []

  // Also keep track of original relationships for the save function
  const relationshipsById = {}
  relationships
    ?.filter((rel) => rel.fieldType !== 'NO_RELATION') // Exclude NO_RELATION entries
    ?.forEach((rel) => {
      if (rel?.id) {
        const { relKey, order } = parseRelKey(rel?.relKey)
        relationshipsById[rel.id] = {
          id: rel.id,
          otherPersonId: rel.otherPersonId,
          relKey,
          order,
        }
      }
    })

  return { relationships: relDefaults, relationshipsById }
}

/**
 * Build default form values for the family members edit UI following the simplified form state structure.
 *
 * Creates a form state that matches the structure defined in USER_INTERACTION.md:
 * - focusedPerson: Contains personId and recordId
 * - relationships: Array of relationship objects with stable formIds
 * - crossRecordResolution: Tracks cross-record strategy decisions
 *
 * @param {Array<Object>} relationships Raw relationships; items may contain: id, relKey, label, value, otherPersonId.
 * @param {Object} person The focused person object with id and recordId.
 * @param {Array<Object>} households Array of household suggestion objects for the household field.
 * @returns {Object} Initial form values object matching the simplified form state structure.
 */
export function prepareFamilyMembersDefaultValues(relationships, person, households = []) {
  const { relationships: relDefaults, relationshipsById } = prepareRelationshipsFields(relationships)

  // Find the household matching the person's recordId or default to MAKE_NEW_RECORD
  const household =
    households.find((hh) => hh.key === person?.recordId) ?? households.find((hh) => hh.key === MAKE_NEW_RECORD)

  return {
    focusedPerson: {
      personId: person?.id || null,
      recordId: person?.recordId || null,
    },
    relationships: relDefaults,
    targetRecordId: person?.recordId || MAKE_NEW_RECORD,
    targetPersonIsPrimary: person?.primary || false,
    names: [],
    // Keep this for backward compatibility with save function
    relationshipsById,
    household,
  }
}

/**
 * Merge related person name suggestions from relationships into an existing autosuggest list.
 *
 * Each relationship contributes a suggestion object of the form: { key, primaryText, group }.
 * New suggestions are only appended if their key does not already exist in `namesAutoSuggest`.
 * The input array is mutated for efficiency and then returned.
 *
 * @param {Object} params Wrapper object.
 * @param {Array<Object>} params.relationships Relationship objects containing `otherPersonId`, `value`, and optional `group`.
 * @param {Array<Object>} params.namesAutoSuggest Existing suggestion entries to enrich.
 * @returns {Array<Object>} The updated (mutated) suggestion array.
 */
export function prepareFamilyMemberNamesAutosuggest({ relationships, namesAutoSuggest }) {
  const newSuggestions =
    relationships?.map((rel) => ({
      key: rel?.otherPersonId,
      primaryText: rel?.value,
      group: rel?.group,
      recordId: rel?.person?.recordId || null,
    })) || []

  newSuggestions.forEach((newSuggestion) => {
    const exists = namesAutoSuggest.some((existing) => existing.key === newSuggestion.key)
    if (!exists) {
      namesAutoSuggest.push(newSuggestion)
    }
  })
  return namesAutoSuggest
}

/**
 * Produce grouped & localized autosuggest options for relationship types respecting directionality.
 *
 * For each relationship type definition in `types.relationships`, generates direction-based entries:
 *  - FIRST and SECOND (when they resolve to distinct labels via `getRelationshipLabel`).
 * Relationships deemed "common" (using {@link getCommonRelationships}) are grouped under
 * "Common Relationships" and sorted before "Other Relationships". Labels are localized using `t`.
 *
 * @param {Object} types Types object from SLS containing an array `relationships`.
 * @param {string} sexValue GEDCOM-X sex code suffix (e.g. 'Male', 'Female').
 * @param {boolean} [includeNoRelation=false] Whether to include a "No Relation" option.
 * @returns {Array<Object>} Sorted suggestion list objects: { key, primaryText, group, sort }.
 */
export const getGroupedGenderedRelTypeSuggestions = (types, sexValue, includeNoRelation = true) => {
  const commonTypes = getCommonRelationships()
  const groupedSuggestions = types?.relationships?.reduce((acc, rel) => {
    const otherPerson = {
      subElements: [
        {
          fieldType: 'SEX_CODE',
          fieldValues: [
            {
              normalizedValues: [
                {
                  resource: `http://gedcomx.org/${sexValue}`,
                },
              ],
            },
          ],
        },
      ],
    }
    let firstRelationshipLabel = ''
    let secondRelationshipLabel = ''
    firstRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'FIRST', typeData: rel })
    secondRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'SECOND', typeData: rel })

    const isCommon = commonTypes.some((common) => common === rel.relType)
    let group = i18n.t('household-details-card.other-group', 'Other Relationships')
    let sort = 2
    if (isCommon) {
      group = i18n.t('household-details-card.common-group', 'Common Relationships')
      sort = 1
    }

    acc.push({
      key: `${rel.relType}_FIRST`,
      primaryText: firstRelationshipLabel,
      group,
      sort,
    })
    if (firstRelationshipLabel !== secondRelationshipLabel) {
      acc.push({
        key: `${rel.relType}_SECOND`,
        primaryText: secondRelationshipLabel,
        group,
        sort,
      })
    }
    return acc
  }, [])
  groupedSuggestions.sort((a, b) => {
    if (a.group === b.group) {
      return a.primaryText.localeCompare(b.primaryText)
    }
    return a.sort - b.sort
  })

  // Optionally include "No Relation" at the end
  if (includeNoRelation) {
    groupedSuggestions.push(NO_RELATION_SUGGESTION)
  }

  return groupedSuggestions
}

/**
 * Split a composite relationship key into base key and order suffix.
 *
 * Relationship keys are expected as: <base>_FIRST or <base>_SECOND. If no underscore is found,
 * returns the original value as `relKey` with an empty `order`.
 *
 * @param {string} relKey Composite relationship key (e.g. 'PARENT_CHILD_FIRST').
 * @returns {{ relKey: string, order: string }} Parsed key parts.
 */
export function parseRelKey(relKey) {
  const lastUnderscoreIndex = relKey?.lastIndexOf('_')
  const relKeyPart = lastUnderscoreIndex !== -1 ? relKey?.substring(0, lastUnderscoreIndex) : relKey
  const order = lastUnderscoreIndex !== -1 ? relKey?.substring(lastUnderscoreIndex + 1) : ''
  return { relKey: relKeyPart, order }
}

/**
 * Extracts person IDs from CREATE actions in a list of dispatches.
 *
 * Iterates through the provided dispatches and collects the IDs of any persons being created.
 * This is useful for identifying new persons that need to be added to a review list.
 *
 * @param {Array<Object>} dispatches Array of SLS dispatch objects.
 * @returns {Array<string>} Array of person IDs created by the dispatches.
 */
export function getPersonIdsToReviewFromCreateActions(dispatches) {
  if (!dispatches || !Array.isArray(dispatches)) {
    return []
  }

  return dispatches
    .filter((dispatch) => dispatch.type === 'CREATE_ELEMENT' && dispatch.value?.elementType === 'PERSON')
    .map((dispatch) => dispatch.value?.id)
    .filter(Boolean)
}

/**
 * @typedef {Object} PendingReviewResult
 * @property {Array<string>} newMemberIds - Person IDs that need review.
 * @property {Array<string>} newMemberIdsWithExistingRelationships - Person IDs with existing relationships in their households.
 */

/**
 * Determines which person IDs from the form relationships are pending review,
 * specifically those linked to other records, and checks if they have existing relationships.
 *
 * @param {Array} images - The array of SLS images.
 * @param {Object} formData - The form data object.
 * @param {Array} formData.formRelationships - List of relationship objects from the form.
 * @param {string} formData.targetRecordId - The ID of the target record.
 * @returns {PendingReviewResult} Object containing arrays of person IDs pending review and those with existing relationships.
 */
export const getPersonIdsPendingReview = (formData, images) => {
  const { relationships, targetRecordId } = formData
  // 1. Determine which relationships need review (i.e. those linked to other records)
  const candidateIdsToReview =
    relationships
      .filter((rel) => !!rel?.recordId && !!rel?.personId && rel.recordId !== targetRecordId)
      ?.map((rel) => rel.personId) || []

  // 2. We need to check if each of those persons have existing relationships in their households
  const slsPersons =
    candidateIdsToReview.map((personId) => getNestedOrFloatingElementById(images, personId)).filter(Boolean) || []
  const personIdsWithExistingRelationships =
    slsPersons
      .filter((slsPerson) => (getNestedSubelementsByType(slsPerson, 'RELATIONSHIP') || []).length > 0)
      ?.map((slsPerson) => slsPerson.id) || []

  return {
    newMemberIds: candidateIdsToReview,
    newMemberIdsWithExistingRelationships: personIdsWithExistingRelationships,
  }
}

/**
 * Helper function to check if any person in the household (main person OR relationships) is marked as primary.
 *
 * @param {Object} params - The parameters object.
 * @param {Array} params.images - The array of SLS images.
 * @param {Object} params.person - The main person object.
 * @param {Object} params.formData - The form data object.
 * @returns {boolean} True if any person in the household is marked as primary.
 */
export function householdHasAnyPrimaryPerson({ images, person, formData }) {
  // Check main person's SLS primary status
  const slsMainPerson = getNestedOrFloatingElementById(images, person.id)
  if (slsMainPerson?.primary) {
    return true
  }

  // Check each non-deleted relationship
  const activeRelationships = formData.relationships.filter((rel) => !rel.isDeleted && rel.personId)

  return activeRelationships.some((rel) => {
    // Check pending changes first (takes precedence)
    if (rel.secondaryPersonPrimaryStatusChange !== null) {
      return rel.secondaryPersonPrimaryStatusChange === true
    }
    // Check current SLS primary status
    const slsSecondaryPerson = getNestedOrFloatingElementById(images, rel.personId)
    return slsSecondaryPerson?.primary === true
  })
}

/**
 * Determines whether to show a primary person selection overlay for a household.
 *
 * Logic:
 * 1. If main person is already marked as primary, no overlay needed
 * 2. If single person household, no overlay needed (will auto-assign)
 * 3. If NEW record with multiple people, show overlay
 * 4. If existing record, check if household already has a primary person
 *
 * @param {Object} params - The parameters object.
 * @param {Object} params.formData - The form data object containing targetPersonIsPrimary, targetRecordId, and relationships.
 * @param {Array} params.images - The array of SLS images.
 * @param {Object} params.person - The main person object.
 * @returns {boolean} True if overlay should be shown, false otherwise.
 */
export function checkIfHouseholdNeedsPrimaryPerson({ formData, images, person }) {
  // Early exit: main person already marked as primary
  if (formData.targetPersonIsPrimary === true) {
    return false
  }

  // Check if any secondary person was marked as primary through the dialog
  const hasSecondaryPrimary = formData.relationships.some(
    (rel) => !rel.isDeleted && rel.secondaryPersonPrimaryStatusChange === true
  )
  if (hasSecondaryPrimary) {
    return false
  }

  // Count active relationships (exclude deleted)
  const activeRelationships = formData.relationships.filter((rel) => !rel.isDeleted && rel.personId)

  // Single person check: no overlay needed (will auto-assign)
  if (activeRelationships.length === 0) {
    return false
  }

  // Multi-person household
  if (formData.targetRecordId === MAKE_NEW_RECORD) {
    // New record needs primary person selection
    return true
  }

  // Existing record: check if household already has any primary person
  return !householdHasAnyPrimaryPerson({ images, person, formData })
}
